/* eslint-disable max-lines */
import { Box, Button, Card, CardContent, Chip, Typography } from '@mui/material';
import RichTextViewer from 'common/components/RichTextViewer';
import { getEventDescription } from 'common/constants/getEventDescription';
import {
  eventTitleMap,
  getEventDotColor,
  getEventKeyInfo,
} from 'common/modules/ShippingTimelineModal';
import { format } from 'date-fns';
import { capitalize, isEmpty } from 'lodash';
import PropTypes from 'prop-types';

import TimelineEventDetails from './TimelineEventDetails';

const timelineDotColor = (item) => {
  if (item.kind === 'note') return 'warning.main';
  if (item.kind === 'request') return item.isProcessSuccess ? 'success.main' : 'error.main';
  if (item.kind === 'event') {
    if (item.isShipmentError) return 'error.main';
    return `${getEventDotColor(item.rawEvent)}.main`;
  }
  return 'primary.main';
};

const TimelineItem = ({
  item,
  idx,
  isLast,
  expandedRequestIds,
  expandedEventIds,
  toggleRequestExpand,
  toggleEventExpand,
  onViewJson,
}) => {
  const eventKeyLines =
    item.kind === 'event'
      ? (() => {
          const ki = getEventKeyInfo(item.rawEvent);
          return Array.isArray(ki) ? ki : [ki];
        })()
      : [];
  const dateText = item.timestamp ? format(new Date(item.timestamp), 'MM/dd/yyyy') : '';
  const timeText = item.timestamp ? format(new Date(item.timestamp), 'hh:mm:ss a') : '';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        px: 2,
        py: 1.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Left — timestamp + performer */}
      <Box
        sx={{
          width: 110,
          flexShrink: 0,
          textAlign: 'right',
          pr: 1.5,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          pt: 0.25,
        }}
      >
        {dateText && (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ display: 'block', lineHeight: 1.5, fontSize: '0.7rem' }}
          >
            {dateText}
          </Typography>
        )}
        {timeText && (
          <Typography
            variant='body2'
            color='primary.main'
            fontWeight={700}
            sx={{ display: 'block', lineHeight: 1.5, fontSize: '0.7rem' }}
          >
            {timeText}
          </Typography>
        )}
        {item.detail && (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ display: 'block', lineHeight: 1.4, fontStyle: 'italic', fontSize: '0.65rem' }}
          >
            {capitalize(item.detail.replace(/\s+/g, '')) ===
            capitalize('System User'.replace(/\s+/g, ''))
              ? 'Auto'
              : item.detail}
          </Typography>
        )}
      </Box>

      {/* Center — dot + connector line */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
          width: 20,
        }}
      >
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: timelineDotColor(item),
            flexShrink: 0,
            mt: 0.4,
          }}
        />
        {!isLast && <Box sx={{ width: 2, flex: 1, bgcolor: 'grey.300', mt: 0.5, minHeight: 12 }} />}
      </Box>

      {/* Right — content */}
      <Box sx={{ flex: 1, minWidth: 0, pl: 1.5 }}>
        {/* Kind chips */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
          {item.kind === 'note' && !item.isShippingRelated && (
            <Chip
              label='Note'
              size='small'
              color='warning'
              sx={{ height: 16, fontSize: '0.65rem', color: 'white' }}
            />
          )}
          {item.kind === 'note' && item.isShippingRelated && (
            <Chip
              label='Shipping Note'
              size='small'
              color='info'
              sx={{ height: 16, fontSize: '0.65rem' }}
            />
          )}
          {item.kind === 'event' && (
            <Chip
              label='Shipping'
              size='small'
              color='info'
              sx={{ height: 16, fontSize: '0.65rem' }}
            />
          )}
          {item.kind === 'event' && item.isShipmentError && (
            <Chip
              label='Error'
              size='small'
              color='error'
              sx={{ height: 16, fontSize: '0.65rem' }}
            />
          )}
          {item.kind === 'request' && (
            <Chip
              label='Request'
              size='small'
              color={item.isProcessSuccess ? 'success' : 'error'}
              sx={{ height: 16, fontSize: '0.65rem' }}
            />
          )}
        </Box>

        {/* Title row */}
        {item.kind !== 'note' && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}
          >
            <Typography
              variant='body2'
              fontWeight={600}
              color={item.kind === 'event' && item.isShipmentError ? 'error.main' : 'text.primary'}
              sx={{ wordBreak: 'break-word' }}
            >
              {item.kind === 'event'
                ? eventTitleMap[item.rawEvent?.auditEventType] ||
                  item.rawEvent?.auditEventType?.replace(/_/g, ' ') ||
                  item.label
                : item.label}
            </Typography>
            {item.kind === 'event' && item.rawEvent?.auditEventData && (
              <Button
                size='small'
                variant='outlined'
                sx={{
                  flexShrink: 0,
                  fontSize: 11,
                  px: 1,
                  py: 0.25,
                  minWidth: 0,
                  color: 'text.secondary',
                  borderColor: 'divider',
                  '&:hover': { borderColor: 'text.secondary' },
                }}
                onClick={() => onViewJson(item.rawEvent.auditEventData)}
              >
                View JSON
              </Button>
            )}
          </Box>
        )}

        {/* Request: view more toggle */}
        {item.kind === 'request' && !isEmpty(item.interactionData) && (
          <Typography
            variant='body2'
            color='primary.main'
            sx={{ cursor: 'pointer', display: 'block', mt: 0.25 }}
            onClick={() => toggleRequestExpand(item.id)}
          >
            {expandedRequestIds.includes(item.id) ? 'Hide' : 'View more >'}
          </Typography>
        )}

        {/* Event: key info lines */}
        {eventKeyLines.filter(Boolean).map((line) => (
          <Typography
            key={line}
            variant='body2'
            color='text.secondary'
            sx={{ display: 'block', fontSize: '0.75rem' }}
          >
            {line}
          </Typography>
        ))}

        {/* Event: expandable diff / rate detail */}
        {item.kind === 'event' && (
          <TimelineEventDetails
            event={item.rawEvent}
            expandedEventIds={expandedEventIds}
            toggleEventExpand={toggleEventExpand}
          />
        )}

        {/* Note markdown content */}
        {item.kind === 'note' && (
          <RichTextViewer
            value={item.label.replace(/\n(?!\n)/g, '\n\n')}
            id={`timeline-note-${idx}`}
          />
        )}

        {/* Request description card */}
        {item.kind === 'request' &&
          !isEmpty(item.interactionData) &&
          expandedRequestIds.includes(item.id) && (
            <Card elevation={0} sx={{ my: 1, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ pb: '12px !important' }}>
                <Typography variant='body2' sx={{ mb: 1.5 }}>
                  {getEventDescription(item.requestDetails, item.eventPayload) || '—'}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size='small'
                    variant='outlined'
                    sx={{
                      fontSize: 11,
                      px: 1,
                      py: 0.25,
                      minWidth: 0,
                      color: 'text.secondary',
                      borderColor: 'divider',
                      '&:hover': { borderColor: 'text.secondary' },
                    }}
                    onClick={() => onViewJson(item.eventPayload)}
                  >
                    View JSON
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
      </Box>
    </Box>
  );
};

TimelineItem.propTypes = {
  item: PropTypes.shape({
    kind: PropTypes.string.isRequired,
    id: PropTypes.string,
    timestamp: PropTypes.string,
    shipstationShippedDate: PropTypes.string,
    label: PropTypes.string,
    detail: PropTypes.string,
    isShipmentError: PropTypes.bool,
    isProcessSuccess: PropTypes.bool,
    isShippingRelated: PropTypes.bool,
    rawEvent: PropTypes.shape({
      auditEventType: PropTypes.string,
      auditEventId: PropTypes.string,
      auditEventData: PropTypes.shape({}),
    }),
    interactionData: PropTypes.oneOfType([
      PropTypes.shape({}),
      PropTypes.arrayOf(PropTypes.shape({})),
    ]),
    eventPayload: PropTypes.shape({}),
    requestDetails: PropTypes.shape({}),
  }).isRequired,
  idx: PropTypes.number.isRequired,
  isLast: PropTypes.bool.isRequired,
  expandedRequestIds: PropTypes.arrayOf(PropTypes.string),
  expandedEventIds: PropTypes.arrayOf(PropTypes.string),
  toggleRequestExpand: PropTypes.func,
  toggleEventExpand: PropTypes.func,
  onViewJson: PropTypes.func,
};

TimelineItem.defaultProps = {
  expandedRequestIds: [],
  expandedEventIds: [],
  toggleRequestExpand: () => {},
  toggleEventExpand: () => {},
  onViewJson: () => {},
};

export default TimelineItem;
